<template>
  <!-- relative, because the dish button stretches to fill it. The hover fill was
       the whole line while only the text was clickable, so the padding and the
       price column lit up and then did nothing. -->
  <div
    class="relative -mx-3 flex h-menu-line items-center gap-3 border-t px-3 py-2.5 pj-tap-row hover:bg-surface-faint"
    :class="isSoldOut && 'opacity-45'"
  >
    <!-- after:inset-0 takes the whole line without nesting a button in a button
         or hanging a click handler on a div. -->
    <button
      class="flex min-w-0 flex-1 flex-col items-start text-left after:absolute after:inset-0"
      type="button"
      @click="emit('select', props.item)"
    >
      <span class="w-full truncate text-title text-ink">{{ props.item.name }}</span>
      <!-- Hint and description share one line, so the height stays fixed. -->
      <span
        v-if="hasDetail"
        class="w-full truncate text-caption text-muted"
      >
        <span
          v-if="venueHint"
          class="font-medium text-ink"
          >{{ venueHint }}</span
        >
        <template v-if="hasHintAndDescription"> · </template>
        {{ props.item.description }}
      </span>
    </button>

    <!-- A real right-hand column, which is the entire reason this view exists.
         A €0.00 item shows nothing rather than a zero: a price column makes
         that the loudest thing on the screen, where a row buries it. -->
    <span
      v-if="isSoldOut"
      class="shrink-0 text-price text-muted"
      >Sold out</span
    >
    <span
      v-else-if="hasPrice"
      class="pj-num shrink-0 text-price text-ink"
      >{{ priceLabel }}</span
    >

    <!-- z-10 to sit above the dish button's stretched target, or adding would
         open the sheet instead. -->
    <button
      v-if="canAdd"
      :aria-label="addLabel"
      class="relative z-10 flex h-tap w-8 shrink-0 items-center justify-center"
      type="button"
      @click="emit('add', props.item)"
    >
      <span
        class="flex h-7 w-7 items-center justify-center rounded-full pj-tap"
        :class="hasCount ? 'bg-primary text-on-primary' : 'bg-surface text-ink'"
      >
        <span
          v-if="hasCount"
          class="pj-num text-item"
          >{{ props.count }}</span
        >
        <PjIcon
          v-else
          :name="PJ_ICON.ADD"
        />
      </span>
    </button>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import type {
  PjMenuLineEmits,
  PjMenuLineProps
} from '@/domains/pjazza/components/molecules/pj-menu-line/PjMenuLine.types.ts'

import { PJ_ICON } from '@/domains/pjazza/components/atoms/pj-icon/PjIcon.types.ts'
import PjIcon from '@/domains/pjazza/components/atoms/pj-icon/PjIcon.vue'
import { formatMenuItemPrice } from '@/domains/pjazza/utils/formatMenuItemPrice.ts'

/**
 * The compact line, for menus too long to browse.
 *
 * What it buys over the default row is a right-aligned price column and roughly
 * twice the dishes per screen; what it gives up is photography. That is the
 * right trade on a 37-section menu, a wine list, or a retail catalogue, and the
 * wrong one on a short menu somebody has photographed well.
 *
 * No artwork at all, deliberately — not even a thumbnail where one exists. A
 * slot reserved per section made the price column jump sideways between
 * sections, since a section with no photographs reserved nothing; reserving it
 * for the whole menu instead would leave a permanent empty gutter down every
 * drinks list. Both are a poor price for a 52px thumbnail in the one view whose
 * entire purpose is a column you can run an eye down. Photography belongs to
 * the other view, which is what the toggle is for.
 */
const props = defineProps<PjMenuLineProps>()

const emit = defineEmits<PjMenuLineEmits>()

const isSoldOut = computed(() => !props.item.available)

const canAdd = computed(() => !isSoldOut.value && props.item.isOrderable)

/** Shown only on a dish in its schedule that the diner still cannot add here. */
const venueHint = computed(() => (isSoldOut.value || props.item.isOrderable ? null : props.inVenueHint))

const hasDetail = computed(() => !!venueHint.value || !!props.item.description)

const hasHintAndDescription = computed(() => !!venueHint.value && !!props.item.description)

const priceLabel = computed(() => formatMenuItemPrice(props.item))

const hasCount = computed(() => (props.count ?? 0) > 0)

// Threls Office has a dish priced at zero. In a grid that is a quiet oddity; in
// a price column it shouts, so it says nothing at all instead.
const hasPrice = computed(() => props.item.price.amount > 0)

const addLabel = computed(() =>
  hasCount.value ? `${props.item.name}, ${props.count} in your order` : `Add ${props.item.name}`
)
</script>
